import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { getRoles } from '@/api/rolesApi';
import { UserRole } from '@/components/layouts/NavigationConfig';
import { Group } from '@/types/shared';

interface UseUserRoleReturn {
  userRole: UserRole;
  userGroup: Group | null;
  isLoading: boolean;
  error: string | null;
}

export const useUserRole = (): UseUserRoleReturn => {
  const { user, isLoading: authLoading } = useAuth();
  const [userGroup, setUserGroup] = useState<Group | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Always use the exact role from JWT token, no mapping needed
  const userRole: UserRole = user?.role?.toLowerCase() as UserRole || 'school_admin';
  const isLoading = authLoading; // Use auth loading state directly

  // Debug logging
  if (user?.role) {
    console.log('🔍 User role from token:', user.role);
    console.log('✅ Direct role (lowercase):', userRole);
  }

  return {
    userRole,
    userGroup,
    isLoading,
    error
  };
};

// Helper function to map role names from API to navigation roles
const mapRoleNameToNavigationRole = (roleName: string): UserRole => {
  const roleMap: { [key: string]: UserRole } = {
    // Common role names from groups/roles API
    'super_admin': 'super_admin',
    'Super Admin': 'super_admin',
    'SUPER_ADMIN': 'super_admin',
    'Platform Admin': 'super_admin',
    
    'school_admin': 'school_admin',
    'School Admin': 'school_admin',
    'SCHOOL_ADMIN': 'school_admin',
    'School Administrator': 'school_admin',
    'Administrator': 'school_admin',
    
    'teacher': 'teacher',
    'Teacher': 'teacher',
    'TEACHER': 'teacher',
    'Educator': 'teacher',
    'Instructor': 'teacher',
    
    'parent': 'parent',
    'Parent': 'parent',
    'PARENT': 'parent',
    'Guardian': 'parent',
    
    'driver': 'driver',
    'Driver': 'driver',
    'DRIVER': 'driver',
    'Bus Driver': 'driver',
    'Transport Driver': 'driver',
    
    'student': 'student',
    'Student': 'student',
    'STUDENT': 'student',
    'Learner': 'student'
  };

  return roleMap[roleName] || 'school_admin'; // Default fallback changed from super_admin
};

// Helper function for backward compatibility with role field
const mapRoleStringToNavigationRole = (role: string): UserRole => {
  console.log('🔍 Mapping role string to navigation role:', role);
  
  if (!role) {
    console.log('❌ No role provided, defaulting to school_admin');
    return 'school_admin'; // Changed default from super_admin
  }

  const normalizedRole = role.toLowerCase().replace(/[_\s]/g, '');
  console.log('📝 Normalized role:', normalizedRole);
  
  if (normalizedRole.includes('superadmin') || normalizedRole.includes('platformadmin')) {
    console.log('✅ Mapped to super_admin');
    return 'super_admin';
  }
  if (normalizedRole.includes('schooladmin') || normalizedRole.includes('administrator')) {
    console.log('✅ Mapped to school_admin');
    return 'school_admin';
  }
  if (normalizedRole.includes('teacher') || normalizedRole.includes('educator')) {
    console.log('✅ Mapped to teacher');
    return 'teacher';
  }
  if (normalizedRole.includes('parent') || normalizedRole.includes('guardian')) {
    return 'parent';
  }
  if (normalizedRole.includes('driver')) {
    return 'driver';
  }
  if (normalizedRole.includes('student') || normalizedRole.includes('learner')) {
    return 'student';
  }

  console.log('⚠️ No role match found, defaulting to school_admin. Role was:', role);
  return 'school_admin'; // Default fallback changed from super_admin
};